/*
 * Element Kin reading engine.
 *
 * Turns a birth date and optional 2-hour birth window into a Four Pillars
 * (Ba Zi) chart, scores the Five Elements with hidden stems, and picks the
 * element to recommend. Pure functions; depends on lunar-javascript's Solar.
 *
 * Works in the browser (window.EKReading) and in Node (module.exports) so
 * the logic can be tested outside the theme.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory()
  } else {
    root.EKReading = factory()
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict"

  var STEM_ELEMENT = {
    甲: "wood", 乙: "wood", 丙: "fire", 丁: "fire", 戊: "earth",
    己: "earth", 庚: "metal", 辛: "metal", 壬: "water", 癸: "water",
  }

  // Hidden stems in each branch: main qi first, then middle and residual qi.
  var BRANCH_HIDDEN = {
    子: ["癸"], 丑: ["己", "癸", "辛"], 寅: ["甲", "丙", "戊"], 卯: ["乙"],
    辰: ["戊", "乙", "癸"], 巳: ["丙", "庚", "戊"], 午: ["丁", "己"],
    未: ["己", "丁", "乙"], 申: ["庚", "壬", "戊"], 酉: ["辛"],
    戌: ["戊", "辛", "丁"], 亥: ["壬", "甲"],
  }
  var HIDDEN_WEIGHTS = [0.6, 0.3, 0.1]

  var ELEMENTS = ["wood", "fire", "earth", "metal", "water"]

  // Generating cycle: wood feeds fire, fire makes earth, earth bears metal,
  // metal carries water, water nourishes wood.
  var GENERATES = { wood: "fire", fire: "earth", earth: "metal", metal: "water", water: "wood" }
  var GENERATED_BY = { fire: "wood", earth: "fire", metal: "earth", water: "metal", wood: "water" }
  // Controlling cycle: wood parts earth, earth dams water, water quenches
  // fire, fire melts metal, metal cuts wood.
  var CONTROLS = { wood: "earth", earth: "water", water: "fire", fire: "metal", metal: "wood" }

  // Month branch carries extra weight because it sets the season.
  var MONTH_BRANCH_BONUS = 0.5

  /** Two-hour windows, keyed by an hour inside each window. */
  var HOUR_WINDOWS = [
    { hour: 0, label: "11 PM – 1 AM" }, { hour: 2, label: "1 AM – 3 AM" },
    { hour: 4, label: "3 AM – 5 AM" }, { hour: 6, label: "5 AM – 7 AM" },
    { hour: 8, label: "7 AM – 9 AM" }, { hour: 10, label: "9 AM – 11 AM" },
    { hour: 12, label: "11 AM – 1 PM" }, { hour: 14, label: "1 PM – 3 PM" },
    { hour: 16, label: "3 PM – 5 PM" }, { hour: 18, label: "5 PM – 7 PM" },
    { hour: 20, label: "7 PM – 9 PM" }, { hour: 22, label: "9 PM – 11 PM" },
  ]

  function emptyScores() {
    return { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 }
  }

  function addBranch(scores, branch, multiplier) {
    var hidden = BRANCH_HIDDEN[branch]
    for (var i = 0; i < hidden.length; i++) {
      scores[STEM_ELEMENT[hidden[i]]] += HIDDEN_WEIGHTS[i] * multiplier
    }
  }

  /**
   * @param {object} Solar lunar-javascript Solar class
   * @param {{year:number, month:number, day:number, hour?:number|null}} input
   *   hour is any hour inside the 2-hour window, or null when unknown.
   */
  function readChart(Solar, input) {
    var hasHour = input.hour !== null && input.hour !== undefined && input.hour !== ""
    var hour = hasHour ? Number(input.hour) : 12
    var solar = Solar.fromYmdHms(input.year, input.month, input.day, hour, 0, 0)
    var lunar = solar.getLunar()
    var chart = lunar.getEightChar()

    var pillars = [
      { name: "year", stem: chart.getYearGan(), branch: chart.getYearZhi() },
      { name: "month", stem: chart.getMonthGan(), branch: chart.getMonthZhi() },
      { name: "day", stem: chart.getDayGan(), branch: chart.getDayZhi() },
    ]
    if (hasHour) {
      pillars.push({ name: "hour", stem: chart.getTimeGan(), branch: chart.getTimeZhi() })
    }

    var scores = emptyScores()
    for (var i = 0; i < pillars.length; i++) {
      var p = pillars[i]
      scores[STEM_ELEMENT[p.stem]] += 1
      addBranch(scores, p.branch, p.name === "month" ? 1 + MONTH_BRANCH_BONUS : 1)
    }

    var total = 0
    for (var e in scores) total += scores[e]
    var percent = {}
    for (var k in scores) percent[k] = Math.round((scores[k] / total) * 100)

    var dayMaster = STEM_ELEMENT[chart.getDayGan()]
    return {
      hasHour: hasHour,
      pillars: pillars,
      scores: scores,
      percent: percent,
      dayMaster: dayMaster,
      dayMasterStem: chart.getDayGan(),
      zodiac: lunar.getYearShengXiaoByLiChun(),
      recommendation: recommend(scores, dayMaster),
    }
  }

  /** Label each element relative to an even share of the chart. */
  function levels(scores) {
    var total = 0
    for (var e in scores) total += scores[e]
    var even = total / 5
    var out = {}
    for (var k in scores) {
      var r = scores[k] / even
      out[k] = r === 0 ? "missing" : r < 0.6 ? "low" : r > 1.6 ? "strong" : "balanced"
    }
    return out
  }

  /**
   * Choose the element to recommend.
   * 1. Missing element: recommend it ("add what is missing").
   * 2. Day Master weak (self + resource below even share): recommend the
   *    resource element that feeds it.
   * 3. Day Master strong: recommend the output element that drains it.
   * 4. Otherwise: recommend the weakest element.
   */
  function recommend(scores, dayMaster) {
    var lv = levels(scores)
    var total = 0
    for (var e in scores) total += scores[e]

    var missing = ELEMENTS.filter(function (x) { return lv[x] === "missing" })
    if (missing.length) {
      missing.sort(function (a, b) { return scores[a] - scores[b] })
      return { element: missing[0], reason: "missing", levels: lv }
    }

    var support = scores[dayMaster] + scores[GENERATED_BY[dayMaster]]
    var share = support / total
    if (share < 0.35) {
      return { element: GENERATED_BY[dayMaster], reason: "support-day-master", levels: lv }
    }
    if (share > 0.55) {
      return { element: GENERATES[dayMaster], reason: "drain-day-master", levels: lv }
    }

    var weakest = ELEMENTS.slice().sort(function (a, b) { return scores[a] - scores[b] })[0]
    return { element: weakest, reason: "weakest", levels: lv }
  }

  return {
    ELEMENTS: ELEMENTS,
    HOUR_WINDOWS: HOUR_WINDOWS,
    GENERATES: GENERATES,
    GENERATED_BY: GENERATED_BY,
    CONTROLS: CONTROLS,
    readChart: readChart,
    recommend: recommend,
    levels: levels,
  }
})
